import React, { useEffect, useRef, useState } from 'react';
import { useCountdown } from '../hooks/useCountdown';
import { X, Mail, Smartphone, ArrowRight, ArrowLeft, Lock, Eye, EyeOff, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import { sendVerificationCode, sendSmsCode, resetPassword, resetPasswordByPhone } from '../services/auth';
import BrandLogo from './BrandLogo';
import { getErrorMessage } from '../utils/error';

interface ForgotPasswordModalProps {
  onClose: () => void;
  onBackToLogin: () => void;
  mode?: 'reset' | 'change';
}

type ResetMethod = 'email' | 'phone';
type Step = 'method' | 'verify' | 'newPassword' | 'success';

export default function ForgotPasswordModal({ onClose, onBackToLogin, mode = 'reset' }: ForgotPasswordModalProps) {
  const { t } = useLanguage();
  const [method, setMethod] = useState<ResetMethod>('email');
  const [step, setStep] = useState<Step>('method');

  // 表单状态
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 加载状态
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, startCountdown] = useCountdown(60);

  // 错误信息
  const [error, setError] = useState('');
  const [isShaking, setIsShaking] = useState(false);
  const shakeTimerRef = useRef<number | null>(null);

  const triggerShake = () => {
    setIsShaking(true);
    if (shakeTimerRef.current !== null) {
      window.clearTimeout(shakeTimerRef.current);
    }
    shakeTimerRef.current = window.setTimeout(() => {
      setIsShaking(false);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (shakeTimerRef.current !== null) {
        window.clearTimeout(shakeTimerRef.current);
      }
    };
  }, []);

  // 发送验证码
  const handleSendCode = async () => {
    if (sendingCode || countdown > 0) {
      return;
    }

    if (method === 'email' && !email) {
      setError(t.auth.enterEmail);
      triggerShake();
      return;
    }
    if (method === 'phone' && !phone) {
      setError(t.auth.enterPhone);
      triggerShake();
      return;
    }

    setSendingCode(true);
    setError('');
    try {
      if (method === 'email') {
        await sendVerificationCode(email);
      } else {
        await sendSmsCode(phone, 'reset_password');
      }
      startCountdown();
      setStep('verify');
    } catch (error: unknown) {
      setError(getErrorMessage(error, t.auth.sendFailed));
      triggerShake();
    } finally {
      setSendingCode(false);
    }
  };

  // 验证验证码并进入设置新密码
  const handleVerifyCode = () => {
    if (!code) {
      setError(t.auth.enterCode);
      triggerShake();
      return;
    }
    if (code.length < 4) {
      setError(t.auth.invalidCode);
      triggerShake();
      return;
    }
    setError('');
    setStep('newPassword');
  };

  // 重置密码
  const handleResetPassword = async () => {
    if (!newPassword) {
      setError(t.auth.enterNewPassword);
      triggerShake();
      return;
    }
    if (newPassword.length < 6) {
      setError(t.auth.passwordMinLength);
      triggerShake();
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t.auth.passwordMismatch);
      triggerShake();
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (method === 'email') {
        await resetPassword({ email, verificationCode: code, newPassword });
      } else {
        await resetPasswordByPhone({ phone, code, newPassword });
      }
      setStep('success');
    } catch (error: unknown) {
      setError(getErrorMessage(error, t.auth.resetFailed));
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  // 返回上一步
  const handleBack = () => {
    setError('');
    if (step === 'verify') setStep('method');
    else if (step === 'newPassword') setStep('verify');
  };

  const getStepTitle = () => {
    switch (step) {
      case 'method': return mode === 'change' ? t.auth.changePassword : t.auth.forgotPasswordTitle;
      case 'verify': return t.auth.verifyIdentity;
      case 'newPassword': return t.auth.setNewPassword;
      case 'success': return mode === 'change' ? t.auth.passwordChanged : t.auth.resetSuccessful;
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'method': return mode === 'change' ? t.auth.chooseChangeMethod : t.auth.chooseResetMethod;
      case 'verify': return `${t.auth.codeSentTo} ${method === 'email' ? email : phone}`;
      case 'newPassword': return t.auth.setNewPasswordDesc;
      case 'success': return mode === 'change' ? t.auth.passwordChangedDesc : t.auth.passwordResetDesc;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 isolate">
      {/* Backdrop: Premium Light Glassmorphism */}
      <div
        className="absolute inset-0 bg-white/35 backdrop-blur-[8px] transform-gpu transition-opacity duration-300 pointer-events-auto"
        style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
        onClick={onClose}
      >
        {/* Subtle radial light focus */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(255,255,255,0.25)_100%)] pointer-events-none"></div>
      </div>

      <div className="relative w-full max-w-lg bg-white rounded-modal shadow-modal-heavy overflow-hidden flex flex-col h-auto max-h-[90vh] animate-in fade-in zoom-in-95 duration-500 border border-white z-10">

        {/* Header decoration */}
        <div className="absolute top-[-100px] left-1/2 -translate-x-1/2 w-[400px] h-[200px] bg-brand-200/20 rounded-full blur-[80px] pointer-events-none"></div>
        <div className="absolute bottom-[-100px] right-[-100px] w-80 h-80 bg-prestige-300/10 rounded-full blur-[80px] pointer-events-none"></div>

        {/* Center Form */}
        <div className="flex-1 p-8 md:p-12 flex flex-col relative bg-white">
          <div className="absolute inset-0 bg-noise opacity-[0.02] pointer-events-none"></div>

          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2.5 text-ink-muted hover:text-brand-900 hover:bg-surface-hover rounded-full transition-all z-20"
          >
            <X size={20} />
          </button>

          <BrandLogo variant="light" className="mb-8" />

          <div className="relative z-10 mb-8 mt-2">
            <div className="flex items-center gap-3 mb-4">
              {step !== 'method' && step !== 'success' && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-1.5 text-xs font-bold text-ink-muted hover:text-brand-900 transition-colors uppercase tracking-widest group"
                >
                  <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
                  {t.auth.backToLogin}
                </button>
              )}
            </div>
            <h2 className="text-4xl font-serif font-bold text-brand-900 mb-2">{getStepTitle()}</h2>
            <p className="text-ink-muted text-sm font-light">{getStepDescription()}</p>
          </div>

          {/* Error Message */}
          <div className={`transition-all duration-300 overflow-hidden relative z-20 ${error ? 'max-h-20 opacity-100 mb-8' : 'max-h-0 opacity-0 mb-0'}`}>
            <div className="flex items-center gap-2 p-3 bg-brand-900 border border-transparent rounded-xl text-surface-light text-xs font-bold tracking-wide shadow-lg">
              <AlertCircle size={14} className="shrink-0" />
              {error}
            </div>
          </div>

          {/* Step 1: Method Selection */}
          {step === 'method' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex p-1 bg-surface-DEFAULT rounded-full border border-surface-border">
                <button
                  type="button"
                  onClick={() => { setMethod('email'); setError(''); }}
                  className={`flex-1 py-3 text-xs font-semibold tracking-wide rounded-full transition-all flex items-center justify-center gap-2
                  ${method === 'email'
                      ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border'
                      : 'text-ink-muted hover:text-brand-900'}`}
                >
                  <Mail size={16} /> {t.auth.resetMethod.email}
                </button>
                <button
                  type="button"
                  onClick={() => { setMethod('phone'); setError(''); }}
                  className={`flex-1 py-3 text-xs font-semibold tracking-wide rounded-full transition-all flex items-center justify-center gap-2
                  ${method === 'phone'
                      ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border'
                      : 'text-ink-muted hover:text-brand-900'}`}
                >
                  <Smartphone size={16} /> {t.auth.resetMethod.phone}
                </button>
              </div>

              <div className="relative grid grid-cols-1">
                <div className={`col-start-1 row-start-1 space-y-2 transition-all duration-300 ease-in-out transform ${method === 'email' ? 'opacity-100 translate-x-0 z-10 pointer-events-auto delay-100' : 'opacity-0 -translate-x-8 z-0 pointer-events-none'}`}>
                  <label className="text-xs font-bold text-ink-muted uppercase tracking-widest ml-1 block">
                    {t.auth.email}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-12 px-4 rounded-lg border border-surface-border bg-surface-DEFAULT text-sm font-sans font-semibold focus:bg-white focus:border-surface-divider focus:ring-1 focus:ring-brand-700 transition-all outline-none"
                  />
                </div>
                <div className={`col-start-1 row-start-1 space-y-2 transition-all duration-300 ease-in-out transform ${method === 'phone' ? 'opacity-100 translate-x-0 z-10 pointer-events-auto delay-100' : 'opacity-0 translate-x-8 z-0 pointer-events-none'}`}>
                  <label className="text-xs font-bold text-ink-muted uppercase tracking-widest ml-1 block">
                    {t.auth.phone}
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full h-12 px-4 rounded-lg border border-surface-border bg-surface-DEFAULT text-sm font-sans font-semibold focus:bg-white focus:border-surface-divider focus:ring-1 focus:ring-brand-700 transition-all outline-none"
                  />
                </div>
              </div>

              <div className="pt-4">
                <button
                  onClick={handleSendCode}
                  disabled={sendingCode || countdown > 0}
                  className={`w-full relative h-14 ${isShaking ? 'animate-shake bg-brand-900 shadow-lg' : 'bg-brand-600 hover:bg-brand-700 shadow-cta-dark hover:shadow-2xl hover:-translate-y-0.5'} text-surface-light rounded-full text-lg font-medium tracking-wide transition-all duration-300 group flex items-center justify-center gap-3 overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed`}
                >
                  <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>
                  {sendingCode ? (
                    <Loader2 size={20} className="animate-spin relative z-10" />
                  ) : countdown > 0 ? (
                    <span className="relative z-10">{`${countdown}s`}</span>
                  ) : (
                    <>
                      <span className="relative z-10">{t.auth.sendCodeBtn}</span>
                      <ArrowRight size={18} className="relative z-10 group-hover:translate-x-1 transition-transform stroke-[2.5]" />
                    </>
                  )}
                </button>

                <div className="mt-8 text-center">
                  <button onClick={onBackToLogin} className="text-brand-900 font-semibold text-sm hover:underline">
                    {t.auth.rememberPassword}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Verification */}
          {step === 'verify' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="space-y-2">
                <label className="text-xs font-bold text-ink-muted uppercase tracking-widest ml-1 block">
                  {t.auth.verificationCode}
                </label>
                <div className="flex gap-4">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    maxLength={6}
                    className="flex-1 h-12 px-4 rounded-lg border border-surface-border bg-surface-DEFAULT text-sm font-sans font-semibold focus:bg-white focus:border-surface-divider focus:ring-1 focus:ring-brand-700 transition-all outline-none text-center tracking-[0.2em] font-bold text-brand-900"
                  />
                  <button
                    onClick={handleSendCode}
                    disabled={sendingCode || countdown > 0}
                    className="px-6 h-12 bg-surface-DEFAULT border border-surface-border rounded-lg text-xs font-bold text-brand-900 hover:bg-surface-border disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap min-w-[100px] flex items-center justify-center"
                  >
                    {sendingCode ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : countdown > 0 ? (
                      `${countdown}s`
                    ) : (
                      t.auth.resend
                    )}
                  </button>
                </div>
              </div>

              <div className="pt-4">
                <button
                  onClick={handleVerifyCode}
                  className="w-full relative h-14 bg-brand-600 hover:bg-brand-700 shadow-cta-dark text-surface-light rounded-full text-lg font-medium tracking-wide transition-all duration-300 group flex items-center justify-center gap-3 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>
                  <span className="relative z-10">{t.auth.nextStep}</span>
                  <ArrowRight size={18} className="relative z-10 group-hover:translate-x-1 transition-transform stroke-[2.5]" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: New Password */}
          {step === 'newPassword' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-ink-muted uppercase tracking-widest ml-1 block">
                    {t.auth.newPassword}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full h-12 px-4 pr-12 rounded-lg border border-surface-border bg-surface-DEFAULT text-sm font-sans font-semibold focus:bg-white focus:border-surface-divider focus:ring-1 focus:ring-brand-700 transition-all outline-none"
                    />
                    {newPassword.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-muted hover:text-brand-900 transition-colors"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-ink-muted uppercase tracking-widest ml-1 block">
                    {t.auth.confirmPassword}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full h-12 px-4 pr-12 rounded-lg border border-surface-border bg-surface-DEFAULT text-sm font-sans font-semibold focus:bg-white focus:border-surface-divider focus:ring-1 focus:ring-brand-700 transition-all outline-none"
                    />
                    {confirmPassword.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-muted hover:text-brand-900 transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <button
                  onClick={handleResetPassword}
                  disabled={loading}
                  className={`w-full relative h-14 ${isShaking ? 'animate-shake bg-brand-900 shadow-lg' : 'bg-brand-600 hover:bg-brand-700 shadow-cta-dark hover:shadow-2xl hover:-translate-y-0.5'} text-surface-light rounded-full text-lg font-medium tracking-wide transition-all duration-300 group flex items-center justify-center gap-3 overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed`}
                >
                  <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>
                  {loading ? (
                    <Loader2 size={20} className="animate-spin relative z-10" />
                  ) : (
                    <>
                      <Lock size={18} className="relative z-10 shrink-0" />
                      <span className="relative z-10">{mode === 'change' ? t.auth.changePassword : t.auth.resetNow}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div className="flex flex-col items-center justify-center py-10 animate-in fade-in zoom-in-95 duration-700 text-center">
              <div className="w-24 h-24 bg-brand-50 rounded-full flex items-center justify-center mb-10 relative">
                <div className="absolute inset-0 bg-brand-400/10 rounded-full animate-ping"></div>
                <div className="w-16 h-16 bg-brand-600 rounded-full flex items-center justify-center text-white shadow-lg relative z-10">
                  <CheckCircle size={36} className="stroke-[2.5px]" />
                </div>
              </div>

              <h3 className="text-2xl font-serif font-bold text-brand-900 mb-4">
                {t.auth.successTitle}
              </h3>
              <p className="text-ink-secondary text-[15px] font-normal leading-relaxed max-w-[280px] mb-12 opacity-80">
                {mode === 'change' ? t.auth.successChangeDesc : t.auth.successResetDesc}
              </p>

              <button
                onClick={onBackToLogin}
                className="inline-flex items-center gap-2 px-10 h-14 bg-brand-900 text-white rounded-full text-base font-bold shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 group"
              >
                <span>{t.auth.backToLogin}</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



